(() => {
  if (window.TCEDIYCartGroups) return;

  const state = { applying: false, scheduled: false };

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency || ""}`.trim();
    }
  }

  function groupId(item) {
    return item.properties?._DIY_design_group_id || item.properties?.["_DIY design_group_id"] || "";
  }

  function componentId(item) {
    return item.properties?._DIY_component_id || item.properties?.["_DIY component_id"] || "Component";
  }

  function isDesignLead(entry) {
    const properties = entry.item.properties || {};
    return Boolean(properties["_DIY preview snapshot"] || properties["Bead sequence"] || properties["Bead Sequence"]);
  }

  function installSequenceToggles(root) {
    root.querySelectorAll(".diy-cart-sequence__toggle").forEach((toggle) => {
      if (toggle.dataset.sequenceToggleReady === "true") return;
      toggle.dataset.sequenceToggleReady = "true";
      toggle.addEventListener("click", (event) => {
        const details = toggle.closest("details.diy-cart-sequence");
        if (!details) return;
        event.preventDefault();
        event.stopPropagation();
        details.open = toggle.classList.contains("diy-cart-sequence__toggle--more");
      });
    });
  }

  function installDetails(row, group) {
    const detailsCell = row.querySelector(".cart-item__details");
    if (!detailsCell) return;
    detailsCell.querySelector(".diy-cart-group__details")?.remove();
    const details = document.createElement("details");
    details.className = "diy-cart-group__details";
    const summary = document.createElement("summary");
    summary.textContent = "View bracelet components";
    const list = document.createElement("ul");
    const components = new Map();
    group.items.forEach(({ item }) => {
      const id = componentId(item);
      const current = components.get(id) || { id, label: item.variant_title || item.title || id, quantity: 0 };
      current.quantity += item.quantity;
      components.set(id, current);
    });
    components.forEach((component) => {
      const entry = document.createElement("li");
      entry.textContent = `${component.label} × ${component.quantity}`;
      list.append(entry);
    });
    details.append(summary, list);
    detailsCell.append(details);
  }

  function rowFor(entry, drawer) {
    if (!drawer && entry.item.key) {
      const row = document.querySelector(`#main-cart-items .cart-item[data-line-key="${CSS.escape(entry.item.key)}"]`);
      if (row) return row;
    }
    return document.getElementById(`${drawer ? "CartDrawer-Item" : "CartItem"}-${entry.index + 1}`);
  }

  function detailsFor(entry) {
    if (entry.item.key) {
      const row = document.querySelector(`.cart-item__diy-details-row[data-line-key="${CSS.escape(entry.item.key)}"]`);
      if (row) return row;
    }
    return document.getElementById(`CartItem-DiyDetails-${entry.index + 1}`);
  }

  async function updateGroupQuantity(group, targetQuantity, control) {
    if (control.dataset.updating === "true") return;
    const nextQuantity = Math.max(1, Math.min(99, Number(targetQuantity) || 1));
    if (nextQuantity === group.designQuantity) return;

    control.dataset.updating = "true";
    control.querySelectorAll("button").forEach((button) => {
      button.disabled = true;
    });

    const updates = Object.fromEntries(
      group.items.map(({ item }) => {
        const unitQuantity = Math.max(1, Math.round(item.quantity / group.designQuantity));
        return [item.key, unitQuantity * nextQuantity];
      })
    );

    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error("Group quantity update failed");
      window.location.reload();
    } catch (error) {
      control.dataset.updating = "false";
      control.querySelectorAll("button").forEach((button) => {
        button.disabled = false;
      });
      const errorTarget = document.getElementById("cart-errors");
      if (errorTarget) errorTarget.textContent = "Could not update this bracelet. Please try again.";
    }
  }

  function buildGroupQuantity(group) {
    const control = document.createElement("div");
    control.className = "diy-cart-group__quantity";
    control.setAttribute("role", "group");
    control.setAttribute("aria-label", "Custom bracelet quantity");

    const minus = document.createElement("button");
    minus.type = "button";
    minus.className = "diy-cart-group__quantity-button";
    minus.setAttribute("aria-label", "Decrease custom bracelet quantity");
    minus.textContent = "−";

    const value = document.createElement("span");
    value.className = "diy-cart-group__quantity-value";
    value.setAttribute("aria-live", "polite");
    value.textContent = String(group.designQuantity);

    const plus = document.createElement("button");
    plus.type = "button";
    plus.className = "diy-cart-group__quantity-button";
    plus.setAttribute("aria-label", "Increase custom bracelet quantity");
    plus.textContent = "+";

    minus.disabled = group.designQuantity <= 1;
    minus.addEventListener("click", () => updateGroupQuantity(group, group.designQuantity - 1, control));
    plus.addEventListener("click", () => updateGroupQuantity(group, group.designQuantity + 1, control));
    control.append(minus, value, plus);
    return control;
  }

  function consolidatePageGroup(leadRow, detailsRow, group, currency) {
    const content = detailsRow?.querySelector(".diy-cart-details__content");
    const heading = content?.querySelector(".diy-cart-details__heading");
    const actions = leadRow.querySelector(".diy-cart-group__actions");
    if (!content || !heading || !actions) return;

    content.querySelectorAll(".diy-cart-details__field").forEach((field) => {
      const label = field.querySelector(".diy-cart-details__label")?.textContent.trim().toLowerCase();
      if (label === "estimated design total") field.remove();
    });
    actions.querySelector(".diy-cart-group__count")?.remove();

    let header = content.querySelector(".diy-cart-details__consolidated-header");
    if (!header) {
      header = document.createElement("div");
      header.className = "diy-cart-details__consolidated-header";
      heading.replaceWith(header);
      header.append(heading);
    }
    let total = header.querySelector(".diy-cart-details__total");
    if (!total) {
      total = document.createElement("div");
      total.className = "diy-cart-details__total";
    }
    total.innerHTML = `<strong>${money(group.totalCents, currency)}</strong>`;
    header.querySelector(".diy-cart-group__quantity")?.remove();
    const quantity = buildGroupQuantity(group);
    header.querySelector(".diy-cart-group__actions")?.remove();
    header.append(quantity, total, actions);
    detailsRow.classList.add("cart-item__diy-details-row--standalone");
    leadRow.hidden = true;
  }

  function installRemoveButton(row, group, drawer) {
    const cell = row.querySelector(".cart-item__quantity");
    if (!cell) return;
    cell.querySelector("quantity-popover")?.remove();
    cell.querySelector(".cart-item__quantity-wrapper")?.remove();
    let wrap = cell.querySelector(".diy-cart-group__actions");
    if (!wrap) {
      wrap = document.createElement("div");
      wrap.className = "diy-cart-group__actions";
      cell.prepend(wrap);
    }
    wrap.innerHTML = "";
    const count = document.createElement("span");
    count.className = "diy-cart-group__count";
    count.textContent = `${group.componentCount} components`;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "button button--tertiary diy-cart-group__remove";
    button.setAttribute("aria-label", "Remove DIY design");
    button.innerHTML = `
      <span class="svg-wrapper" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6h18"></path>
          <path d="M8 6V4h8v2"></path>
          <path d="M19 6l-1 14H6L5 6"></path>
          <path d="M10 11v5M14 11v5"></path>
        </svg>
      </span>
      <span>Remove</span>
    `;
    button.addEventListener("click", async () => {
      if (button.disabled) return;
      button.disabled = true;
      const updates = Object.fromEntries(group.items.map((entry) => [entry.item.key, 0]));
      try {
        const response = await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates })
        });
        if (!response.ok) throw new Error("Group removal failed");
        window.location.reload();
      } catch (error) {
        button.disabled = false;
        const errorTarget = document.getElementById(drawer ? "CartDrawer-CartErrors" : "cart-errors");
        if (errorTarget) errorTarget.textContent = "Could not remove this bracelet. Please try again.";
      }
    });
    wrap.append(count, button);
  }

  function presentGroup(group, currency, drawer) {
    const lead = group.items.find(isDesignLead) || group.items[0];
    const leadRow = rowFor(lead, drawer);
    if (!leadRow) return;
    leadRow.hidden = false;
    leadRow.classList.add("cart-item--diy-group-lead");
    const title = leadRow.querySelector(".cart-item__name");
    if (title) {
      title.textContent = "Custom Crystal Bracelet";
      title.removeAttribute("href");
    }
    leadRow.querySelectorAll(".cart-item__price-wrapper .price, .cart-item__totals .price").forEach((node) => {
      node.textContent = money(group.totalCents, currency);
    });
    const restoredDetails = leadRow.querySelector(".diy-cart-details");
    leadRow.querySelectorAll(".cart-item__details > .product-option:not(.diy-cart-details)").forEach((node) => node.remove());
    const detailsRow = drawer ? null : detailsFor(lead);
    if (!restoredDetails && !detailsRow) installDetails(leadRow, group);
    installSequenceToggles(leadRow);
    installRemoveButton(leadRow, group, drawer);

    if (!drawer) {
      leadRow.querySelector(".cart-item__media")?.replaceChildren();
      leadRow.classList.add("cart-item--diy-group-lead-no-thumbnail");
      if (detailsRow) {
        detailsRow.hidden = false;
        detailsRow.classList.add("cart-item__diy-details-row--grouped");
        leadRow.after(detailsRow);
        installSequenceToggles(detailsRow);
        consolidatePageGroup(leadRow, detailsRow, group, currency);
      }
    }

    group.items.filter((entry) => entry !== lead).forEach((entry) => {
      const row = rowFor(entry, drawer);
      if (row) row.hidden = true;
      if (!drawer) {
        const details = detailsFor(entry);
        if (details) details.hidden = true;
      }
    });
  }

  async function apply() {
    if (state.applying) return;
    state.applying = true;
    try {
      const response = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!response.ok) return;
      const cart = await response.json();
      const groups = new Map();
      cart.items.forEach((item, index) => {
        const id = groupId(item);
        if (!id) return;
        const group = groups.get(id) || {
          id,
          items: [],
          totalCents: 0,
          componentCount: 0,
          unitComponentCount: 0,
          designQuantity: 1
        };
        group.items.push({ item, index });
        group.totalCents += item.final_line_price;
        group.componentCount += item.quantity;
        const declaredComponentCount = Number(
          item.properties?._DIY_group_component_count ||
          item.properties?.["_DIY group_component_count"] ||
          item.properties?.["Component count"] ||
          item.properties?.["Bead count"] ||
          0
        );
        if (declaredComponentCount > 0) {
          group.unitComponentCount = Math.max(group.unitComponentCount, declaredComponentCount);
        }
        groups.set(id, group);
      });
      groups.forEach((group) => {
        if (group.unitComponentCount > 0 && group.componentCount % group.unitComponentCount === 0) {
          group.designQuantity = Math.max(1, group.componentCount / group.unitComponentCount);
        }
        presentGroup(group, cart.currency, false);
        presentGroup(group, cart.currency, true);
      });
      document.documentElement.dataset.diyCartGroupCount = String(groups.size);
    } finally {
      state.applying = false;
    }
  }

  function schedule() {
    if (state.scheduled) return;
    state.scheduled = true;
    requestAnimationFrame(() => {
      state.scheduled = false;
      apply();
    });
  }

  window.TCEDIYCartGroups = { apply, schedule };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", apply, { once: true });
  } else {
    apply();
  }
  document.addEventListener("cart:refresh", () => {
    schedule();
    window.setTimeout(schedule, 400);
  });
  const cartRoot = document.getElementById("main-cart-items");
  if (cartRoot) {
    new MutationObserver(() => {
      if (cartRoot.querySelector(".cart-item--diy:not([hidden])")) schedule();
    }).observe(cartRoot, { childList: true, subtree: true });
  }
})();
